import React, { useEffect, useState } from 'react';
import { PaymentGatewayTable } from '../../../components/merchants/paymentGateway/PaymentGatewayTable';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'rsuite';
import { useNavigate, useParams } from 'react-router-dom';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { PaymentGatewayFilters } from '../../../components/merchants/MerchantPage/PaymentGatewayFilters';
import { SectionWrapper } from '../../../components/SectionWrapper';
import { usePaymentContext } from '../../../store/payments/payments-context';
import {
	addNewPaymentGateway,
	fetchPaymentGatewayData,
	fetchPaymentGatewayTypes,
} from '../../../store/payments/actions/providers';
import { ErrorNotificationComponent } from '../../../components/application/ErrorNotificationComponent';
import { BUTTON_TYPE } from '../../../components/ButtonComponent';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';

export const MerchantPaymentGatewayPage = (): JSX.Element => {
	const { t } = useTranslation();
	const { id: merchantId } = useParams<{ id: string }>();
	const navigate = useNavigate();

	// Local state for payment active status
	const [paymentActiveStatus, setPaymentActiveStatus] = useState<string | null>('1');

	const { paymentContextState: {
		paymentGatewayType,
		isPaymentGatewayTypeLoading
	}, dispatch } = usePaymentContext();
	const { merchantState: { merchantData, isMerchantDataLoading }} = useMerchants();

	useEffect(() => {
		void fetchPaymentGatewayData({ dispatch, mid: merchantId });
	}, []);

	useEffect(() => {
		if (merchantData?.partner?.id) {
			void fetchPaymentGatewayTypes({ dispatch, pid: merchantData?.partner?.id });
		}
	}, [merchantData]);

	const renderButton = (props, ref): JSX.Element => {
		return (
			<button
				{...props}
				ref={ref}
				className="btn-primary btn fw-bolder letter-spacing"
			>
				Add
			</button>
		);
	};

	const handleSelect = async (paymentGatewayTypeId): Promise<void> => {
		try {
			await addNewPaymentGateway({ dispatch, gatewayId: paymentGatewayTypeId, mid: merchantId });
		} catch (e) {
			console.log(e);
		}
	};

	const headerAdditionalBlock = (
		<Dropdown renderToggle={renderButton} placement="bottomEnd" disabled={!merchantData?.partner?.id || !paymentGatewayType?.length}>
			{
				paymentGatewayType?.map(item => (
					<Dropdown.Item
						key={item?.id}
						onClick={(): Promise<void> => handleSelect(item?.id)}
						// onSelect={(eventKey: string, event) => handleSelect(item?.id)}
					>
						{item?.name}
					</Dropdown.Item>
				))
			}
		</Dropdown>
	);

	const getErrorText = (): string | JSX.Element => {
		if (!merchantData?.partner?.id) {
			return `Adding a gateway is not possible because this merchant is not assigned to a partner. Please assign the merchant to a partner with processing capabilities to enable gateway configuration.`;
		}
		if (!paymentGatewayType?.length) {
			return (
				<span>
					Adding a gateway to this merchant is not possible because
					<span className="fw-bolder mx-1">{merchantData?.partner?.name}</span>
					currently lacks payment processing capabilities. Ensure that the partner has the necessary processing setup before assigning a gateway to the merchant.
				</span>
			);
		}
	};

	const getActionName = (): string => {
		if (!merchantData?.partner?.id) {
			return 'Assign a partner ';
		}
		if (!paymentGatewayType?.length) {
			return 'Providers list';
		}
	};

	const getAction = (): void => {
		if (!merchantData?.partner?.id) {
			return navigate(`/accounts/merchants/${merchantId}/${MERCHANTS_TABS.BUSINESS}`);
		}
		if (!paymentGatewayType?.length && merchantData?.partner?.id) {
			return navigate(`/admin/providers/processing?isActive=null&partnerId=${merchantData?.partner?.id}`);
		}
	};


	return (
		<>
			{
				(!merchantData?.partner?.id || !paymentGatewayType?.length) && !isPaymentGatewayTypeLoading && !isMerchantDataLoading && (
					<ErrorNotificationComponent
						title="Attention"
						text={getErrorText()}
						primaryButtonName={getActionName()}
						containerAdditionalClass="mb-5"
						primaryButtonAdditionalClass="no-letter-spacing"
						primaryButtonType={BUTTON_TYPE.FAKE_LINK}
						primaryButtonAction={getAction}
					/>
				)
			}
			<SectionWrapper
				title={t('PAYMENTS_GATEWAY.TABLE_TITLE')}
				sectionAdditionalClass="min-height-200"
				headerAdditionalBlock={headerAdditionalBlock}
				filterSection={
					<PaymentGatewayFilters
						paymentActiveStatus={paymentActiveStatus}
						setPaymentActiveStatus={setPaymentActiveStatus}
					/>
				}
				filterSectionAdditionalClass="pt-5 pb-2"
				isCardBody
			>
				<PaymentGatewayTable
					paymentActiveStatus={paymentActiveStatus}
					setPaymentActiveStatus={setPaymentActiveStatus}
				/>
			</SectionWrapper>
		</>
	);
};
